"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

// ── Types ────────────────────────────────────────────────

export type NotificationType = "success" | "error" | "warning" | "info";

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  /** Auto-dismiss duration in ms. `0` = persist until closed. Default: 5000 */
  duration?: number;
  /** Timestamp for ordering */
  createdAt: number;
}

export interface NotifyOptions {
  type?: NotificationType;
  title: string;
  message?: string;
  /** ms — 0 means persist. Defaults: success/info/warning=5000, error=0 */
  duration?: number;
}

interface NotificationContextValue {
  notifications: Notification[];
  notify: (options: NotifyOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

// ── Defaults ─────────────────────────────────────────────

const DEFAULT_DURATIONS: Record<NotificationType, number> = {
  success: 5000,
  info: 5000,
  warning: 5000,
  error: 0, // errors persist
};

const MAX_VISIBLE = 5;

// ── Context ──────────────────────────────────────────────

const NotificationContext = createContext<NotificationContextValue | null>(null);

let idCounter = 0;

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  const dismiss = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    for (const timer of timersRef.current.values()) clearTimeout(timer);
    timersRef.current.clear();
    setNotifications([]);
  }, []);

  const notify = useCallback(
    (options: NotifyOptions): string => {
      const id = `notif-${++idCounter}`;
      const type = options.type ?? "success";
      const duration = options.duration ?? DEFAULT_DURATIONS[type];

      const notification: Notification = {
        id,
        type,
        title: options.title,
        ...(options.message != null ? { message: options.message } : {}),
        duration,
        createdAt: Date.now(),
      };

      setNotifications((prev) => {
        const next = [...prev, notification];
        // Evict oldest beyond MAX_VISIBLE
        if (next.length > MAX_VISIBLE) {
          const removed = next.shift()!;
          const timer = timersRef.current.get(removed.id);
          if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(removed.id);
          }
        }
        return next;
      });

      if (duration > 0) {
        const timer = setTimeout(() => {
          timersRef.current.delete(id);
          setNotifications((prev) => prev.filter((n) => n.id !== id));
        }, duration);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [], // dismiss is stable — no dep needed
  );

  const value = useMemo(
    () => ({ notifications, notify, dismiss, dismissAll }),
    [notifications, notify, dismiss, dismissAll],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return ctx;
}
